import React, { useState } from 'react';
import { X, ShieldCheck, Crown, Sparkles, Check, Lock, Cloud, Key } from 'lucide-react';
import { User } from '../types';
import { api } from '../api/client';

interface AuthModalProps {
  user: User | null;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ user, onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [googleName, setGoogleName] = useState('');

  const handleDemoLogin = async () => {
    setIsLoading(true);
    try {
      const loggedUser = await api.demoLogin(
        googleName || 'Dean (Master Grower)',
        googleEmail || 'dean@watchthis.work'
      );
      onSuccess(loggedUser);
      onClose();
    } catch (e: any) {
      alert('Login error: ' + e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePro = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const newStatus = await api.togglePremium(!user.is_premium);
      onSuccess({ ...user, is_premium: newStatus });
    } catch (e: any) {
      alert(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'linear-gradient(135deg, #10b981, #065f46)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Crown size={16} color="#fff" />
            </div>
            <h2 style={{ fontSize: '1.35rem', color: 'var(--text-main)' }}>
              {user ? 'Account & Subscription' : 'Sign In / Pro Access'}
            </h2>
          </div>
          <button className="btn btn-secondary btn-icon" onClick={onClose} style={{ padding: '0.4rem' }}>
            <X size={18} />
          </button>
        </div>

        {/* Free vs Pro Comparison */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.85rem', marginBottom: '1.5rem' }}>
          <div
            style={{
              padding: '1rem',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: 6 }}>
              <span className="badge badge-amber">FREE TIER</span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Anonymous</span>
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-sub)', marginBottom: '0.75rem' }}>
              ntfy.sh-style obfuscation URLs for everyone.
            </p>
            <ul style={{ listStyle: 'none', fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={12} color="var(--accent-emerald)" /> Any slug (e.g. /my-grow-192)
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={12} color="var(--accent-emerald)" /> Full Calendar & Timeline views
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={12} color="var(--accent-emerald)" /> No password or login required
              </li>
            </ul>
          </div>

          <div
            style={{
              padding: '1rem',
              borderRadius: 'var(--radius-md)',
              background: 'rgba(16, 185, 129, 0.08)',
              border: '1px solid var(--border-emerald)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: 6 }}>
              <span className="badge badge-emerald">PRO VIP</span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Owner Cloud</span>
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--accent-emerald-light)', marginBottom: '0.75rem' }}>
              Google Auth with Cloudflare D1 & R2 Vault.
            </p>
            <ul style={{ listStyle: 'none', fontSize: '0.8rem', color: 'var(--text-main)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <li style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={12} color="var(--accent-emerald-light)" /> <strong>Private Locked Jobs</strong>
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={12} color="var(--accent-emerald-light)" /> High-Res Video/Photo Vault
              </li>
              <li style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Check size={12} color="var(--accent-emerald-light)" /> Custom Domain Support
              </li>
            </ul>
          </div>
        </div>

        {user ? (
          <div>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.03)',
                padding: '1rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-subtle)',
                marginBottom: '1.25rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{user.name}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{user.email}</div>
                </div>
                <span className={`badge ${user.is_premium ? 'badge-emerald' : 'badge-amber'}`}>
                  {user.is_premium ? 'PRO ACTIVE' : 'FREE TIER'}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                className={`btn ${user.is_premium ? 'btn-secondary' : 'btn-primary'}`}
                onClick={handleTogglePro}
                disabled={isLoading}
              >
                <Sparkles size={14} />
                <span>{user.is_premium ? 'Switch to Free Tier' : 'Upgrade to Pro VIP'}</span>
              </button>

              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Quick Google Sign In */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div className="form-group">
                <label className="form-label">Sign in with Google Account</label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Your Name (e.g. Dean White)"
                    value={googleName}
                    onChange={(e) => setGoogleName(e.target.value)}
                  />
                  <input
                    type="email"
                    className="form-input"
                    placeholder="google.email@gmail.com"
                    value={googleEmail}
                    onChange={(e) => setGoogleEmail(e.target.value)}
                  />
                </div>

                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%', padding: '0.8rem' }}
                  onClick={handleDemoLogin}
                  disabled={isLoading}
                >
                  <Key size={16} />
                  <span>{isLoading ? 'Authenticating...' : 'Sign In with Google (Pro Access)'}</span>
                </button>
              </div>
            </div>

            <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-sub)' }}>
              Encrypted credentials stored in Cloudflare D1 with instant session tokens.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
